import React from 'react';
import { Box, Button, Chip, Drawer, IconButton, Paper, Stack, Typography, useMediaQuery, ToggleButtonGroup, ToggleButton, Divider, CardMedia } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import ChatIcon from '@mui/icons-material/Chat';
import MapIcon from '@mui/icons-material/Map';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

import { AssistantConversation } from '../components/planner/AssistantConversation';
import { GradientLoadingAnimation } from '../components/planner/GradientLoadingAnimation';
import { ExperienceDetailDrawer } from '../components/planner/ExperienceDetailDrawer';
import { usePlanner } from '../contexts/PlannerContext';
import { formatChatTimestamp } from '../services/chatApi';

// Pool of quick prompt suggestions for trip planning
const PROMPT_POOL = [
  'Plan a 4-day romantic escape in Paris',
  'Create a 5-day exploration of Barcelona',
  'Design a 3-day foodie adventure in Bangkok',
  'Plan a 4-day trip to Santorini',
  'Generate a 4-day art and culture tour in Florence',
  'Create a 5-day trip to Venice',
  'Plan a long-weekend wine tour in Cape Town',
  'Design a 4-day trip to Amsterdam',
  'Generate a 4-day winter getaway in Reykjavik',
  'Plan a 3-day trip to Ubud Bali',
  'Create a 3-day cherry blossom trip to Kyoto',
  'Design a 4-day historic journey through Rome',
  'Plan a weekend culture trip to Lisbon',
  'Generate a 5-day trip to Vienna',
  'Create a 4-day family trip to Orlando',
  'Plan a 5-day cultural immersion in Marrakech',
  'Design a 5-day trip to Istanbul',
  'Generate a 3-day luxury retreat in Dubai',
  'Plan a 4-day trip to Edinburgh',
  'Create a weekend culinary escape in Mexico City',
];

// Function to randomly select N prompts from the pool
const getRandomPrompts = (count: number = 3): string[] => {
  const shuffled = [...PROMPT_POOL].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
};

export const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const {
    messages,
    chats,
    chatsLoading,
    currentChatId,
    selectChat,
    startNewChat,
    loading,
    loadingPhase,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isGeneratingTripPlan: _isGeneratingTripPlan,
    travelData,
    sections,
    destinationImageUrls,
    openExperience,
    activeExperience,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isDestinationInfoLoading: _isDestinationInfoLoading,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isMultiCityMode: _isMultiCityMode,
    attachedTrips,
    activeChatTripId,
    markTripAsActive,
    currentSearchId,
  } = usePlanner();
  const { isSignedIn } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [quickPrompts] = React.useState(() => getRandomPrompts(3));
  const [activeTab, setActiveTab] = React.useState<'chat' | 'current-trip'>('chat');
  const [activatingTripId, setActivatingTripId] = React.useState<string | null>(null);
  const showHeroPrompts = messages.length === 0;
  const navHeight = { xs: 56, sm: 64 };
  const toggleTop = { xs: navHeight.xs + 16, sm: navHeight.sm + 16 };

  // Track if we have an active trip being generated or already generated
  const hasActiveTripContent = loading || Boolean(activeChatTripId) || attachedTrips.length > 0 || (travelData && sections.length > 0);

  const activeSearchId = currentSearchId ?? activeChatTripId ?? undefined;

  const formatTripDates = (trip: { start_date?: string; end_date?: string; date_range?: string }) => {
    if (trip.date_range) return trip.date_range;
    if (trip.start_date && trip.end_date) {
      return `${new Date(trip.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${new Date(trip.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    return null;
  };

  const handleActivateTrip = async (searchId: string) => {
    setActivatingTripId(searchId);
    try {
      await markTripAsActive(searchId);
    } catch (error) {
      console.error('Failed to activate trip for chat', error);
    } finally {
      setActivatingTripId(null);
    }
  };

  const renderAttachedTrips = () => {
    if (!attachedTrips.length) {
      return null;
    }

    const sortedTrips = [...attachedTrips].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

    return (
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: { xs: 2, md: 2.5 },
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          backgroundColor: alpha(theme.palette.primary.main, 0.02),
          position: 'relative',
          width: '100%',
          boxSizing: 'border-box',
          maxWidth: '100%',
          mx: 'auto',
        }}
      >
        <Stack spacing={1.5}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            flexWrap="wrap"
            rowGap={0.5}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.4 }}>
              Trips in this chat
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Activate one to view it here
            </Typography>
          </Stack>

          <Stack spacing={1}>
            {sortedTrips.map((trip) => {
              const isActive = trip.search_id === activeChatTripId;
              const isBusy = activatingTripId === trip.search_id;
              const dateLabel = formatTripDates(trip);

              return (
                <Box
                  key={trip.search_id}
                  sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: 1.5,
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    p: 1.5,
                    borderRadius: 2.5,
                    border: `1px solid ${alpha(theme.palette.primary.main, isActive ? 0.35 : 0.12)}`,
                    backgroundColor: alpha(theme.palette.primary.main, isActive ? 0.08 : 0.04),
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {trip.destination_display || trip.destination}
                    </Typography>
                    {dateLabel && (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {dateLabel}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.75 }}>
                      {typeof trip.total_days === 'number' && trip.total_days > 0 && (
                        <Chip size="small" label={`${trip.total_days} day${trip.total_days === 1 ? '' : 's'}`} sx={{ height: 22, fontSize: '0.75rem' }} />
                      )}
                      {typeof trip.total_activities === 'number' && trip.total_activities > 0 && (
                        <Chip size="small" label={`${trip.total_activities} activities`} sx={{ height: 22, fontSize: '0.75rem' }} />
                      )}
                      {typeof trip.total_restaurants === 'number' && trip.total_restaurants > 0 && (
                        <Chip size="small" label={`${trip.total_restaurants} dining spots`} sx={{ height: 22, fontSize: '0.75rem' }} />
                      )}
                    </Stack>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {isActive ? (
                      <Chip
                        size="small"
                        color="success"
                        label="Active"
                        sx={{ height: 28, fontWeight: 700, fontSize: '0.8rem', backgroundColor: alpha(theme.palette.success.main, 0.12) }}
                      />
                    ) : (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleActivateTrip(trip.search_id)}
                        disabled={isBusy}
                        sx={{ textTransform: 'none', fontWeight: 700, minWidth: 120 }}
                      >
                        {isBusy ? 'Activating...' : 'Activate'}
                      </Button>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Stack>
      </Paper>
    );
  };


  const handleStartNewChat = async () => {
    await startNewChat();
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const handleSelectChat = async (chatId: string) => {
    await selectChat(chatId);
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const renderSidebarContent = () => (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 0,
        borderRight: isMobile ? 'none' : `1px solid ${alpha(theme.palette.divider, 0.8)}`,
        backgroundColor: theme.palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: '100%',
        overflow: 'hidden',
        color: theme.palette.text.primary,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{
        px: 1.5,
        pt: 1,
        pb: 1.5,
        flexShrink: 0,
        position: 'sticky',
        top: 0,
        zIndex: 10,
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
      }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: 0.75, color: alpha(theme.palette.text.primary, 0.8) }}
        >
          Recent Chats
        </Typography>
        <Button
          size="small"
          variant="text"
          onClick={handleStartNewChat}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            minWidth: 'auto',
            px: 1.5,
            py: 0.5,
            fontSize: '0.875rem',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
            color: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.16),
            },
          }}
        >
          New
        </Button>
      </Stack>

      <Stack spacing={0.5} sx={{
        flexGrow: 1,
        overflowY: 'auto',
        px: 1.5,
        pb: 1.5,
        minHeight: 0,
        // Hide scrollbar
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      }}>
        {chatsLoading ? (
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.875rem' }}>
            Loading chats...
          </Typography>
        ) : chats.filter(chat => (chat.message_count ?? 0) > 0 || (chat.attached_trip_count ?? 0) > 0 || Boolean(chat.last_message_preview?.trim())).length ? (
          chats
            .filter((chat) => (chat.message_count ?? 0) > 0 || (chat.attached_trip_count ?? 0) > 0 || Boolean(chat.last_message_preview?.trim()))
            .map((chat) => {
            const isActive = chat.chat_id === currentChatId;
            const title = chat.title || chat.active_trip_destination || 'New Chat';
            return (
              <Box
                key={chat.chat_id}
                onClick={() => handleSelectChat(chat.chat_id)}
                sx={{
                  p: 1.25,
                  borderRadius: 2,
                  backgroundColor: isActive
                    ? alpha(theme.palette.primary.main, 0.15)
                    : alpha(theme.palette.primary.main, 0.02),
                  border: isActive ? `1px solid ${alpha(theme.palette.primary.main, 0.5)}` : '1px solid transparent',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease, border 0.2s ease',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: theme.palette.text.primary,
                  }}
                >
                  {title}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    mt: 0.25,
                  }}
                >
                  {chat.last_message_preview || 'No messages yet'}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} alignItems="center">
                  <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>
                    {formatChatTimestamp(chat.last_activity_at)}
                  </Typography>
                  {chat.attached_trip_count > 0 && (
                    <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>
                      • {chat.attached_trip_count} trip{chat.attached_trip_count > 1 ? 's' : ''}
                    </Typography>
                  )}
                </Stack>
              </Box>
            );
          })
        ) : (
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.875rem' }}>
            No chats yet. Start one to see it here.
          </Typography>
        )}
      </Stack>
    </Paper>
  );


  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' },
        height: 'auto',
        width: '100%',
        backgroundColor: theme.palette.background.paper,
        display: 'flex',
        overflow: 'visible',
      }}
    >
      {!isMobile && isSignedIn && (
        <Box sx={{
          width: 280,
          flexShrink: 0,
          position: 'fixed',
          top: { xs: '56px', sm: '64px' },
          left: 0,
          height: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' },
          zIndex: 1,
          overflow: 'hidden',
        }}>{renderSidebarContent()}</Box>
      )}

      {isMobile && isSignedIn && (
        <Drawer
          anchor="left"
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: 280,
            },
          }}
        >
          {renderSidebarContent()}
        </Drawer>
      )}

      <Box
        sx={{
          flex: 1,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          minHeight: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' },
          height: 'auto',
          backgroundColor: theme.palette.background.paper,
          overflowX: 'hidden',
          overflowY: 'visible',
          marginLeft: isSignedIn ? { lg: '280px' } : 0,
          zIndex: 1,
          width: '100%',
          maxWidth: '100vw',
          boxSizing: 'border-box',
        }}
      >
        {isMobile && isSignedIn && (
          <IconButton
            onClick={() => setSidebarOpen(true)}
            sx={{
              position: 'fixed',
              top: 72,
              left: 16,
              zIndex: (theme) => theme.zIndex.appBar + 3,
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              backdropFilter: 'blur(8px)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.2),
              },
            }}
          >
            <MenuRoundedIcon />
          </IconButton>
        )}

          <Box
           sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'hidden',
          overflowY: 'visible',
          width: '100%',
          maxWidth: '100vw',
          boxSizing: 'border-box',
        }}
      >
          {/* Tab switcher */}
        {hasActiveTripContent && (
           <Box
             sx={{
               position: 'fixed',
               top: toggleTop,
               left: '50%',
               transform: 'translateX(-50%)',
               zIndex: (theme) => theme.zIndex.appBar + 1,
               px: { xs: 1, md: 0 },
               pt: 0,
               pb: 0.5,
               display: 'flex',
               justifyContent: 'center',
               width: '100%',
               maxWidth: '100%',
               mx: 'auto',
               overflowX: 'auto',
               boxSizing: 'border-box',
               '&::-webkit-scrollbar': { display: 'none' },
               backgroundColor: 'transparent',
               backdropFilter: 'none',
               borderBottom: 'none',
               pointerEvents: 'none',
             }}
            >
              <ToggleButtonGroup
                value={activeTab}
                exclusive
                onChange={(_, value) => value && setActiveTab(value)}
                size="small"
                sx={{
                  pointerEvents: 'auto',
                  maxWidth: '100%',
                  overflowX: 'auto',
                  '&::-webkit-scrollbar': { display: 'none' },
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderRadius: 3,
                  p: 0,
                  boxShadow: 'none',
                  '& .MuiToggleButtonGroup-grouped': {
                    border: 'none',
                    borderColor: 'transparent',
                    '&:not(:first-of-type)': {
                      borderLeft: 'none',
                      marginLeft: 0,
                    },
                    '&:not(:last-of-type)': {
                      borderRight: 'none',
                      marginRight: 0,
                    },
                  },
                  '& .MuiToggleButton-root': {
                    border: 'none',
                    backgroundColor: alpha(theme.palette.background.paper, 0.9),
                    borderRadius: 999,
                    px: 3,
                    py: 1,
                    mx: 0.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    color: theme.palette.text.secondary,
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.primary.main,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      border: 'none',
                      '&:hover': {
                        backgroundColor: theme.palette.background.paper,
                      },
                    },
                    '&.Mui-focusVisible': {
                      outline: 'none',
                      boxShadow: '0 0 0 2px transparent',
                    },
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    },
                  },
                }}
              >
                <ToggleButton value="chat">
                  <ChatIcon sx={{ mr: 1, fontSize: '1.1rem' }} />
                  Chat
                </ToggleButton>
                <ToggleButton value="current-trip">
                  <MapIcon sx={{ mr: 1, fontSize: '1.1rem' }} />
                  Current Trip
                  {loading && (
                    <Box
                      sx={{
                        ml: 1,
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        animation: 'pulse 1.5s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%, 100%': { opacity: 0.4, transform: 'scale(0.8)' },
                          '50%': { opacity: 1, transform: 'scale(1)' },
                        },
                      }}
                    />
                  )}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          <Box
            sx={{
              flex: 1,
              display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'stretch',
          gap: { xs: 2, lg: 3 },
          px: { xs: 0.75, md: 3 },
          py: { xs: 1, md: 2 },
          boxSizing: 'border-box',
          overflowX: 'hidden',
          overflowY: 'visible',
          minHeight: 0,
          backgroundColor: theme.palette.background.paper,
          // Extra headroom so the fixed/sticky toggle buttons never cover the first chat message
          pt: hasActiveTripContent
            ? { xs: `${toggleTop.xs + 16}px`, sm: `${toggleTop.sm + 28}px` }
            : 0,
          width: '100%',
          maxWidth: '100vw',
        }}
      >
            {activeTab === 'chat' ? (
              <>
                <Box sx={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  backgroundColor: theme.palette.background.paper,
                  pb: { xs: 9, sm: 10 }, // space for sticky input plus a little breathing room
                }}>
                  <AssistantConversation
                    variant="embedded"
                    borderless
                    stickyInput
                    suggestions={showHeroPrompts ? quickPrompts : []}
                    sx={{
                      width: '100%',
                      maxWidth: 960,
                      mx: 'auto',
                      borderRadius: 0,
                      border: 'none',
                      boxShadow: 'none',
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      minHeight: '100%',
                    }}
                  />
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  maxWidth: { xs: '100%', sm: 960 },
                  mx: 'auto',
                  width: '100%',
                  boxSizing: 'border-box',
                  px: { xs: 0.5, sm: 0 },
                  overflowX: 'hidden'
                }}
              >
                {renderAttachedTrips()}
                {loading ? (
                  <GradientLoadingAnimation
                    message={
                      loadingPhase === 'activities_coordination'
                        ? 'Coordinating your activities...'
                        : loadingPhase === 'activities_preliminary'
                        ? 'Finalizing activity details...'
                        : loadingPhase === 'restaurants_preliminary'
                        ? 'Finding dining recommendations...'
                        : 'Creating your travel plan...'
                    }
                    submessage={
                      travelData?.destination
                        ? `Planning your trip to ${travelData.destination}`
                        : 'This usually takes a few moments'
                    }
                  />
                ) : travelData && sections.length > 0 ? (
                  <Paper
                    elevation={0}
                    sx={{
                      overflow: 'visible',
                      borderRadius: 4,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                      backgroundColor: theme.palette.background.paper,
                      textAlign: 'left',
                    }}
                  >
                    {/* Destination Photo */}
                    {destinationImageUrls && destinationImageUrls.length > 0 && (
                      <Box
                        sx={{
                          position: 'relative',
                          width: '100%',
                          height: { xs: 200, sm: 280 },
                          overflow: 'hidden',
                        }}
                      >
                        <CardMedia
                          component="img"
                          image={destinationImageUrls[0]}
                          alt={travelData.destination}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                          onError={(e: any) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        {/* Gradient overlay for text readability */}
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)',
                            p: 3,
                          }}
                        >
                          <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                            {(() => {
                              // For multi-city trips, show all cities
                              if (travelData?.plan_mode === 'multi_city' && travelData?.cities && travelData.cities.length > 1) {
                                const cityNames = travelData.cities.map(c => c.city).filter(Boolean);
                                return cityNames.length > 0 ? cityNames.join(' • ') : travelData.destination;
                              }
                              return travelData.destination;
                            })()}
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={1.5}
                            flexWrap="wrap"
                            sx={{ mb: 1, gap: 1 }}
                          >
                            {travelData.start_date && travelData.end_date && (
                              <Chip
                                size="small"
                                label={
                                  <Typography variant="caption" sx={{ fontWeight: 500, color: 'white' }}>
                                    {new Date(travelData.start_date).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                    })}{' '}
                                    –{' '}
                                    {new Date(travelData.end_date).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                    })}
                                  </Typography>
                                }
                                sx={{
                                  backgroundColor: 'rgba(255,255,255,0.2)',
                                  backdropFilter: 'blur(8px)',
                                  border: '1px solid rgba(255,255,255,0.3)',
                                  height: '24px',
                                }}
                              />
                            )}
                            {travelData.budget && (
                              <Chip
                                size="small"
                                label={
                                  <Typography variant="caption" sx={{ fontWeight: 500, color: 'white', textTransform: 'capitalize' }}>
                                    {travelData.budget.toLowerCase()}
                                  </Typography>
                                }
                                sx={{
                                  backgroundColor: 'rgba(255,255,255,0.2)',
                                  backdropFilter: 'blur(8px)',
                                  border: '1px solid rgba(255,255,255,0.3)',
                                  height: '24px',
                                }}
                              />
                            )}
                            {travelData.restaurants && travelData.restaurants.length > 0 && (
                              <Chip
                                size="small"
                                icon={<RestaurantIcon sx={{ fontSize: '0.875rem !important', color: 'white' }} />}
                                label={
                                  <Typography variant="caption" sx={{ fontWeight: 500, color: 'white' }}>
                                    {travelData.restaurants.length} restaurant{travelData.restaurants.length !== 1 ? 's' : ''}
                    </Typography>
                                }
                                sx={{
                                  backgroundColor: 'rgba(255,255,255,0.2)',
                                  backdropFilter: 'blur(8px)',
                                  border: '1px solid rgba(255,255,255,0.3)',
                                  height: '24px',
                                  '& .MuiChip-icon': {
                                    color: 'white',
                                  },
                                }}
                              />
                            )}
                          </Stack>
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                      {sections.length} day{sections.length !== 1 ? 's' : ''} planned with{' '}
                      {sections.reduce((acc, s) => acc + s.experiences.length, 0)} activities
                    </Typography>
                        </Box>
                      </Box>
                    )}

                    <Box sx={{ p: 4 }}>
                      {/* Destination name if no photo */}
                      {(!destinationImageUrls || destinationImageUrls.length === 0) && (
                        <>
                          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, textAlign: 'center' }}>
                            {(() => {
                              // For multi-city trips, show all cities
                              if (travelData?.plan_mode === 'multi_city' && travelData?.cities && travelData.cities.length > 1) {
                                const cityNames = travelData.cities.map(c => c.city).filter(Boolean);
                                return cityNames.length > 0 ? cityNames.join(' • ') : travelData.destination;
                              }
                              return travelData.destination;
                            })()}
                          </Typography>
                    <Stack
                      direction="row"
                            spacing={1.5}
                      justifyContent="center"
                      flexWrap="wrap"
                            sx={{ mb: 2, gap: 1 }}
                    >
                      {travelData.start_date && travelData.end_date && (
                        <Chip
                                size="small"
                                icon={<CalendarTodayIcon sx={{ fontSize: '0.875rem !important' }} />}
                          label={
                            <Typography variant="caption" sx={{ fontWeight: 500 }}>
                              {new Date(travelData.start_date).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                              })}{' '}
                              –{' '}
                              {new Date(travelData.end_date).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </Typography>
                          }
                          sx={{
                            backgroundColor: alpha(theme.palette.primary.main, 0.08),
                            color: theme.palette.primary.main,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                  height: '24px',
                          }}
                        />
                      )}
                      {travelData.budget && (
                        <Chip
                                size="small"
                                icon={<LocalAtmIcon sx={{ fontSize: '0.875rem !important' }} />}
                          label={
                                  <Typography variant="caption" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
                                    {travelData.budget.toLowerCase()}
                            </Typography>
                          }
                          sx={{
                            backgroundColor: alpha(theme.palette.primary.main, 0.08),
                            color: theme.palette.primary.main,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                  height: '24px',
                          }}
                        />
                      )}
                      {travelData.restaurants && travelData.restaurants.length > 0 && (
                        <Chip
                                size="small"
                                icon={<RestaurantIcon sx={{ fontSize: '0.875rem !important' }} />}
                          label={
                            <Typography variant="caption" sx={{ fontWeight: 500 }}>
                              {travelData.restaurants.length} restaurant{travelData.restaurants.length !== 1 ? 's' : ''}
                            </Typography>
                          }
                          sx={{
                            backgroundColor: alpha(theme.palette.primary.main, 0.08),
                            color: theme.palette.primary.main,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                  height: '24px',
                          }}
                        />
                      )}
                    </Stack>
                          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, textAlign: 'center' }}>
                            {sections.length} day{sections.length !== 1 ? 's' : ''} planned with{' '}
                            {sections.reduce((acc, s) => acc + s.experiences.length, 0)} activities
                          </Typography>
                        </>
                      )}

                      {/* Activity Previews */}
                      {sections.length > 0 && (() => {
                        const allExperiences = sections.flatMap(s => s.experiences);
                        const isMultiCity = travelData?.plan_mode === 'multi_city' && travelData?.cities && travelData.cities.length > 1;
                        
                        if (allExperiences.length > 0) {
                          // Group by city if multi-city trip, also track day numbers
                          const activitiesByCity = new Map<string, { activities: typeof allExperiences; dayNumbers: Set<number> }>();
                          const cityMap = new Map<string, { city: string; city_key: string }>();
                          
                          if (isMultiCity && travelData.cities) {
                            // Build city map
                            travelData.cities.forEach((city) => {
                              cityMap.set(city.city_key, { city: city.city, city_key: city.city_key });
                            });
                            
                            // Group activities by city and track day numbers from sections
                            sections.forEach((section) => {
                              if (!section.dayNumber) return;
                              
                              section.experiences.forEach((experience) => {
                                const cityKey = experience.city_key || experience.city || 'other';
                                if (!activitiesByCity.has(cityKey)) {
                                  activitiesByCity.set(cityKey, { 
                                    activities: [], 
                                    dayNumbers: new Set<number>() 
                                  });
                                }
                                const cityGroup = activitiesByCity.get(cityKey)!;
                                cityGroup.activities.push(experience);
                                cityGroup.dayNumbers.add(section.dayNumber!);
                              });
                            });
                          }
                          
                          const handleActivityClick = (experience: typeof allExperiences[0]) => {
                            if (openExperience) {
                              openExperience(experience);
                            }
                          };
                          
                          return (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>
                                Activities
                              </Typography>
                              
                              {isMultiCity && activitiesByCity.size > 1 ? (
                                // Render grouped by city
                                <Stack spacing={0}>
                                  {Array.from(activitiesByCity.entries()).map(([cityKey, cityData], cityIndex) => {
                                    const cityInfo = cityMap.get(cityKey);
                                    const cityName = cityInfo?.city || cityKey;
                                    const dayNumbers = Array.from(cityData.dayNumbers).sort((a, b) => a - b);
                                    const dayRange = dayNumbers.length === 1 
                                      ? `Day ${dayNumbers[0]}`
                                      : dayNumbers.length === 2 && dayNumbers[1] === dayNumbers[0] + 1
                                      ? `Day ${dayNumbers[0]}-${dayNumbers[1]}`
                                      : dayNumbers.length > 0
                                      ? `Day ${dayNumbers[0]}-${dayNumbers[dayNumbers.length - 1]}`
                                      : '';
                                    
                                    return (
                                      <Box key={cityKey}>
                                        {/* Divider before city (except first) */}
                                        {cityIndex > 0 && (
                                          <Divider 
                                            sx={{ 
                                              my: 3, 
                                              borderColor: alpha(theme.palette.primary.main, 0.2),
                                              borderWidth: 1,
                                            }} 
                                          />
                                        )}
                                        {/* City Header */}
                                        <Box
                                          sx={{
                                            mb: 2,
                                            pb: 1,
                                            borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                          }}
                                        >
                                          <Typography 
                                            variant="body1" 
                                            sx={{ 
                                              fontWeight: 600, 
                                              color: 'text.primary', 
                                              fontSize: '1rem',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 1,
                                              flexWrap: 'wrap',
                                            }}
                                          >
                                            <Box
                                              component="span"
                                              sx={{
                                                width: 4,
                                                height: 4,
                                                borderRadius: '50%',
                                                backgroundColor: theme.palette.primary.main,
                                                flexShrink: 0,
                                              }}
                                            />
                                            {cityName}
                                            {dayRange && (
                                              <Typography 
                                                component="span" 
                                                variant="caption" 
                                                sx={{ 
                                                  fontWeight: 500,
                                                  color: theme.palette.primary.main,
                                                  ml: 0.5,
                                                }}
                                              >
                                                ({dayRange})
                                              </Typography>
                                            )}
                                            <Typography 
                                              component="span" 
                                              variant="caption" 
                                              sx={{ 
                                                fontWeight: 500,
                                                color: 'text.secondary',
                                                ml: 'auto',
                                              }}
                                            >
                                              {cityData.activities.length} {cityData.activities.length === 1 ? 'activity' : 'activities'}
                                            </Typography>
                                          </Typography>
                                        </Box>
                                        <Stack spacing={1}>
                                          {cityData.activities.map((experience, idx) => (
                                            <Box
                                              key={experience.id || idx}
                                              onClick={() => handleActivityClick(experience)}
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                p: 1.5,
                                                borderRadius: 2,
                                                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                                transition: 'all 0.2s ease',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                                  borderColor: alpha(theme.palette.primary.main, 0.2),
                                                  transform: 'translateY(-1px)',
                                                },
                                              }}
                                            >
                                              {experience.imageUrl ? (
                                                <Box
                                                  component="img"
                                                  src={experience.imageUrl}
                                                  alt={experience.name}
                                                  sx={{
                                                    width: 56,
                                                    height: 56,
                                                    borderRadius: 1.5,
                                                    objectFit: 'cover',
                                                    flexShrink: 0,
                                                  }}
                                                  onError={(e: any) => {
                                                    e.target.style.display = 'none';
                                                  }}
                                                />
                                              ) : (
                                                <Box
                                                  sx={{
                                                    width: 56,
                                                    height: 56,
                                                    borderRadius: 1.5,
                                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                  }}
                                                >
                                                  <LocalActivityIcon sx={{ color: alpha(theme.palette.primary.main, 0.5), fontSize: 24 }} />
                                                </Box>
                                              )}
                                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                  variant="body2"
                                                  sx={{
                                                    fontWeight: 600,
                                                    color: 'text.primary',
                                                    mb: 0.25,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                  }}
                                                >
                                                  {experience.name}
                                                </Typography>
                                                {experience.category && (
                                                  <Typography
                                                    variant="caption"
                                                    sx={{
                                                      color: 'text.secondary',
                                                      display: 'block',
                                                      overflow: 'hidden',
                                                      textOverflow: 'ellipsis',
                                                      whiteSpace: 'nowrap',
                                                    }}
                                                  >
                                                    {Array.isArray(experience.category) 
                                                      ? experience.category.join(', ') 
                                                      : experience.category}
                                                  </Typography>
                                                )}
                                              </Box>
                                            </Box>
                                          ))}
                                        </Stack>
                                      </Box>
                                    );
                                  })}
                                </Stack>
                              ) : (
                                // Render all activities in a single list (single city)
                                <Stack spacing={1}>
                                  {allExperiences.map((experience, idx) => (
                                    <Box
                                      key={experience.id || idx}
                                      onClick={() => handleActivityClick(experience)}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        p: 1.5,
                                        borderRadius: 2,
                                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                        transition: 'all 0.2s ease',
                                        cursor: 'pointer',
                                        '&:hover': {
                                          backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                          borderColor: alpha(theme.palette.primary.main, 0.2),
                                          transform: 'translateY(-1px)',
                                        },
                                      }}
                                    >
                                      {experience.imageUrl ? (
                                        <Box
                                          component="img"
                                          src={experience.imageUrl}
                                          alt={experience.name}
                                          sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: 1.5,
                                            objectFit: 'cover',
                                            flexShrink: 0,
                                          }}
                                          onError={(e: any) => {
                                            e.target.style.display = 'none';
                                          }}
                                        />
                                      ) : (
                                        <Box
                                          sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: 1.5,
                                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                          }}
                                        >
                                          <LocalActivityIcon sx={{ color: alpha(theme.palette.primary.main, 0.5), fontSize: 24 }} />
                                        </Box>
                                      )}
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontWeight: 600,
                                            color: 'text.primary',
                                            mb: 0.25,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                          }}
                                        >
                                          {experience.name}
                                        </Typography>
                                        {experience.category && (
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: 'text.secondary',
                                              display: 'block',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap',
                                            }}
                                          >
                                            {Array.isArray(experience.category) 
                                              ? experience.category.join(', ') 
                                              : experience.category}
                                          </Typography>
                                        )}
                                      </Box>
                                    </Box>
                                  ))}
                                </Stack>
                              )}
                            </Box>
                          );
                        }
                        return null;
                      })()}

                      <Box sx={{ textAlign: 'center', mt: 3, mb: 2 }}>
                    <Button
                      variant="contained"
                      onClick={() => navigate(activeSearchId ? `/trip/${activeSearchId}` : '/trip')}
                      sx={{
                        px: { xs: 2, sm: 4 },
                        py: 1.5,
                        borderRadius: 3,
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: { sm: 200 },
                      }}
                    >
                      View Full Itinerary
                    </Button>
                      </Box>
                    </Box>
                  </Paper>
                ) : (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      borderRadius: 4,
                      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      backgroundColor: theme.palette.background.paper,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                      {attachedTrips.length
                        ? 'Activate a trip above to make it the current plan for this chat.'
                        : 'No trip in progress. Start a conversation to plan your next adventure!'}
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => setActiveTab('chat')}
                      sx={{ mt: 2 }}
                    >
                      Go to Chat
                    </Button>
                  </Paper>
                )}
              </Box>
            )}
          </Box>
        </Box>

      </Box>

      {/* Activity Detail Drawer */}
      <ExperienceDetailDrawer
        experience={activeExperience}
        open={Boolean(activeExperience)}
        onClose={() => openExperience?.(null)}
        destination={travelData?.destination}
        onOpenExperience={openExperience}
      />
    </Box>
  );
};
